import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import { numFont } from '../../utils/fonts';
import { formatPriceWithCurrency } from '../../utils/vat';
import CardHeader from '../shared/CardHeader';
import UploadDocumentsDialog from '../purchase-invoices/UploadDocumentsDialog';
import { StatusChip } from '../purchase-invoices/PurchaseChips';
import { PL_API, toNumber } from '../purchase-invoices/purchaseUtils';

/**
 * "Ostolaskut" section of the work order page: purchase invoices and receipts linked to this
 * work order (directly or through a supplier order), "Lisää kuitti", and the invoiced purchase
 * cost next to the part costs recorded on the work order. Informational only; the margin math
 * of the work order and the reports is untouched.
 */
function PurchaseInvoicesSection({ maintenance }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const visible = can(currentOrganization, 'purchase_invoices.view') && currentOrganization?.purchase_ledger_available === true;
  const canManage = can(currentOrganization, 'purchase_invoices.manage');
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const load = useCallback(async () => {
    if (!maintenance?.id) return;
    try {
      const response = await axios.get(`${PL_API}/invoices/`, { params: { maintenance: maintenance.id, tab: 'all', page_size: 50 } });
      setRows(response.data?.results || []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoaded(true);
    }
  }, [maintenance?.id]);

  useEffect(() => {
    if (visible) load();
  }, [visible, load]);

  if (!visible || !maintenance) return null;

  const active = rows.filter((r) => r.status !== 'cancelled');
  const invoicedNet = active.reduce((sum, r) => sum + toNumber(r.total_net), 0);
  const partCost = (maintenance.parts || []).reduce((sum, p) => sum + toNumber(p.cost_price) * toNumber(p.quantity ?? 1), 0);
  const difference = invoicedNet - partCost;

  return (
    <Paper elevation={0} sx={{ mt: 3 }} data-testid="wo-purchase-invoices">
      <CardHeader
        title={t('purchaseLedger.workOrder.title')}
        icon={<ReceiptLongIcon fontSize="small" />}
        action={
          canManage ? (
            <Button size="small" variant="outlined" startIcon={<AddAPhotoIcon />} onClick={() => setUploadOpen(true)} data-testid="wo-add-receipt">
              {t('purchaseLedger.workOrder.addReceipt')}
            </Button>
          ) : null
        }
      />
      <Box sx={{ p: 2 }}>
        {loaded && active.length === 0 && (
          <Typography variant="body2" color="text.secondary">{t('purchaseLedger.workOrder.none')}</Typography>
        )}
        {active.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('purchaseLedger.fields.vendor')}</TableCell>
                  <TableCell>{t('purchaseLedger.fields.invoiceNumber')}</TableCell>
                  <TableCell>{t('purchaseLedger.fields.status')}</TableCell>
                  <TableCell align="right">{t('purchaseLedger.fields.totalGross')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {active.map((r) => (
                  <TableRow key={r.id} hover data-testid={`wo-purchase-invoice-${r.id}`}>
                    <TableCell>
                      <Typography component={RouterLink} to={`/ostolaskut/${r.id}`} variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                        {r.vendor_name || t('purchaseLedger.vendor.unknown')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={numFont}>{r.invoice_number}</TableCell>
                    <TableCell><StatusChip status={r.status} paymentState={r.payment_state} inBatch={r.in_open_batch} /></TableCell>
                    <TableCell align="right" sx={{ ...numFont, fontWeight: 600 }}>{formatPriceWithCurrency(toNumber(r.total_gross))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {(active.length > 0 || partCost > 0) && can(currentOrganization, 'costs.view_purchase_prices') && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, ...numFont }} data-testid="wo-purchase-cost-line">
            {t('purchaseLedger.workOrder.invoicedTotal')} <strong>{formatPriceWithCurrency(invoicedNet)}</strong> · {t('purchaseLedger.workOrder.recordedPartCost')}{' '}
            <strong>{formatPriceWithCurrency(partCost)}</strong> · {t('purchaseLedger.workOrder.difference')}{' '}
            <strong style={{ color: difference > 0.005 ? '#b91c1c' : 'inherit' }}>{formatPriceWithCurrency(difference)}</strong>
          </Typography>
        )}
      </Box>
      <UploadDocumentsDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        maintenanceId={maintenance.id}
        title={t('purchaseLedger.workOrder.addReceipt')}
        onUploaded={() => {
          setSnackbar(t('purchaseLedger.upload.queuedShort'));
          load();
        }}
      />
      <Snackbar open={!!snackbar} autoHideDuration={5000} onClose={() => setSnackbar(null)}>
        <Alert severity="success" onClose={() => setSnackbar(null)}>{snackbar}</Alert>
      </Snackbar>
    </Paper>
  );
}

export default PurchaseInvoicesSection;
